// frontend/src/pages/teams/TeamDetail.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import teamService from '../../services/teamService';
import championshipService from '../../services/championshipService';
import penaltyService from '../../services/penaltyService';
import useAuth from '../../hooks/useAuth';
import './TeamDetail.css';

const TECHNICAL_POSITIONS = ['técnico', 'tecnico', 'auxiliar', 'aux', 'diretor', 'assistente técnico'];

// Função para eliminar duplicatas de jogadores
const getUniquePlayers = (players) => {
  if (!players || !Array.isArray(players)) return [];
  const seen = new Map();
  const unique = [];

  for (const player of players) {
    if (!player || !player._id) continue;
    if (!seen.has(player._id)) {
      seen.set(player._id, true);
      unique.push(player);
    }
  }
  return unique;
};

// Funções auxiliares de imagem
const getPlayerPhotoUrl = (photoFileName) => {
  if (!photoFileName) return '/default-player-photo.png';
  if (photoFileName.startsWith('http://') || photoFileName.startsWith('https://')) {
    return photoFileName;
  }
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '');
  return `${baseUrl}${photoFileName.startsWith('/') ? '' : '/'}${photoFileName}`;
};

const handlePlayerPhotoError = (e) => {
  e.target.src = '/default-player-photo.png';
  e.target.onerror = null;
};

const getTeamLogoUrl = (badge) => {
  if (!badge) return '/default-team-badge.png';
  if (badge.startsWith('http')) return badge;
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '');
  return `${baseUrl}${badge.startsWith('/') ? '' : '/'}${badge}`;
};

const getPixQrSource = (encodedImage) => {
  if (!encodedImage) return null;
  return encodedImage.startsWith('data:image')
    ? encodedImage
    : `data:image/png;base64,${encodedImage}`;
};

function TeamDetail() {
  const { id: teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const championshipIdFromContext =
    searchParams.get('championshipId') ||
    location.state?.championshipId ||
    null;

  const [team, setTeam] = useState(null);
  const [managers, setManagers] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [teamImgSrc, setTeamImgSrc] = useState('/default-team-badge.png');
  const [championshipsUserManages, setChampionshipsUserManages] = useState([]);

  // Estados para Gestão de Gestores
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [managerEmail, setManagerEmail] = useState('');
  const [isSubmittingManager, setIsSubmittingManager] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // Estados para Multas do Time
  const [teamFines, setTeamFines] = useState([]);

  // Estados para Modal de Pagamento Pix
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixPaymentData, setPixPaymentData] = useState(null);
  const [loadingPix, setLoadingPix] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);

  // Estados para o modal de opções da ficha de inscrição
  const [showSheetOptionsModal, setShowSheetOptionsModal] = useState(false);
  const [includePhoneInSheet, setIncludePhoneInSheet] = useState(true);
  const [includeSignatureInSheet, setIncludeSignatureInSheet] = useState(true);
  const [includePositionInSheet, setIncludePositionInSheet] = useState(false);

  const getCreatorId = (entity) => {
    if (!entity) return null;
    if (entity.creator && typeof entity.creator === 'object') {
      return entity.creator._id || entity.creator.id || null;
    }
    return entity.creator || null;
  };

  // Jogadores únicos (sem duplicatas)
  const uniquePlayers = useMemo(() => {
    return getUniquePlayers(team?.players);
  }, [team]);

  // Comissão técnica
  const technicalStaff = useMemo(() => {
    if (!uniquePlayers.length) return [];
    return uniquePlayers.filter(player => {
      const pos = player.position?.toLowerCase() || '';
      return TECHNICAL_POSITIONS.some(role => pos.includes(role));
    });
  }, [uniquePlayers]);

  // Contar apenas jogadores de linha/goleiro
  const countPlayers = useMemo(() => {
    if (!uniquePlayers.length) return 0;
    return uniquePlayers.filter(player => {
      const pos = player.position?.toLowerCase() || '';
      return !TECHNICAL_POSITIONS.some(role => pos.includes(role));
    }).length;
  }, [uniquePlayers]);

  // Filtrar apenas jogadores (excluindo comissão técnica)
  const filteredPlayers = useMemo(() => {
    if (!uniquePlayers.length) return [];
    return uniquePlayers.filter(player => {
      const pos = player.position?.toLowerCase() || '';
      return !TECHNICAL_POSITIONS.some(role => pos.includes(role));
    });
  }, [uniquePlayers]);

  const isRegistrationOpen = useMemo(() => {
    if (!team?.championships || team.championships.length === 0) return false;
    return team.championships.some(champ => champ.registrationOpen === true);
  }, [team]);

  const activeTransferChampionship = useMemo(() => {
    if (!team?.championships) return null;
    return team.championships.find(champ => champ.enablePlayerTransfers === true) || null;
  }, [team]);

  const isTeamOwnerOrManager = useMemo(() => {
    if (!isAuthenticated || !user || !team) return false;
    const userRole = user.role?.toLowerCase() || '';
    if (userRole === 'admin') return true;

    const creatorId = getCreatorId(team);
    const isTeamManager = managers.some(m => String(m._id || m) === String(user._id)) ||
                          team.managers?.some(m => String(m._id || m) === String(user._id));

    return (creatorId && String(user._id) === String(creatorId)) || isTeamManager;
  }, [isAuthenticated, user, team, managers]);

  // Permissão de Auto-Inscrição e Inscrição Regular
  const canRegisterPlayer = useMemo(() => {
    if (!isAuthenticated || !user || !team) return false;
    const championships = team.championships || [];
    if (championships.length === 0) return false;

    const openChampionships = championships.filter(champ => champ.registrationOpen === true);
    if (openChampionships.length === 0) return false;

    const userRole = user.role?.toLowerCase() || '';

    // 1. Admin sempre pode
    if (userRole === 'admin') return true;

    // 2. Diretoria, criador ou gestor do time sempre podem
    const creatorId = getCreatorId(team);
    const isTeamManager = managers.some(m => String(m._id || m) === String(user._id)) ||
                          team.managers?.some(m => String(m._id || m) === String(user._id));
    const isTeamStaffOrDirector = ['diretor', 'técnico', 'tecnico', 'auxiliar técnico', 'auxiliar tecnico', 'gestor'].includes(userRole);

    if ((creatorId && (String(user._id) === String(creatorId) || isTeamManager)) || isTeamStaffOrDirector) {
      return true;
    }

    // 3. Se for um Jogador comum tentando auto-inscrição:
    if (userRole === 'jogador') {
      return openChampionships.some(champ => champ.allowPlayerSelfRegistration !== false);
    }

    return false;
  }, [team, isAuthenticated, user, managers]);

  // Mensagem auxiliar para auto-inscrição bloqueada
  const isSelfRegistrationBlockedForPlayer = useMemo(() => {
    if (!user || user.role?.toLowerCase() !== 'jogador' || !team?.championships) return false;
    const openChampionships = team.championships.filter(champ => champ.registrationOpen === true);
    if (openChampionships.length === 0) return false;
    return openChampionships.every(champ => champ.allowPlayerSelfRegistration === false);
  }, [user, team]);

  // Permissão para gerenciar gestores administrativos do time
  const canManageTeamManagers = useMemo(() => {
    if (!user || !team) return false;
    const userRole = user.role?.toLowerCase() || '';
    if (userRole === 'admin') return true;

    const creatorId = getCreatorId(team);
    if (creatorId && String(user._id) === String(creatorId)) return true;

    if (userRole === 'gestor' && championshipsUserManages.length > 0 && team.championships) {
      return team.championships.some(champ => championshipsUserManages.includes(String(champ._id || champ)));
    }
    return false;
  }, [user, team, championshipsUserManages]);

  // Permissão de edição do time
  const canEditTeam = useMemo(() => {
    if (!user || !team) return false;
    const userRole = user.role?.toLowerCase() || '';
    if (userRole === 'admin') return true;

    const creatorId = getCreatorId(team);
    const isTeamManager = managers.some(m => String(m._id || m) === String(user._id)) ||
                          team.managers?.some(m => String(m._id || m) === String(user._id));

    if (creatorId && (String(user._id) === String(creatorId) || isTeamManager)) return true;

    if (userRole === 'gestor' && championshipsUserManages.length > 0 && team.championships) {
      return team.championships.some(champ => championshipsUserManages.includes(String(champ._id || champ)));
    }
    return false;
  }, [user, team, managers, championshipsUserManages]);

  const canGenerateRegistrationSheet = useMemo(() => canEditTeam, [canEditTeam]);

  const canRemovePlayerFromThisTeam = useCallback(() => {
    return canEditTeam;
  }, [canEditTeam]);

  const canEditPlayerInscription = useCallback(() => {
    return canEditTeam;
  }, [canEditTeam]);

  // Buscar lista de gestores
  const fetchManagersList = useCallback(async () => {
    try {
      setLoadingManagers(true);
      const res = await teamService.getManagers(teamId);
      if (res && res.success) {
        setManagers(res.managers || []);
        if (res.creator) {
          setTeam(prev => prev ? { ...prev, creator: res.creator } : prev);
        }
      }
    } catch (err) {
      console.error('Erro ao buscar gestores do time:', err);
    } finally {
      setLoadingManagers(false);
    }
  }, [teamId]);

  // Carregar dados completos do time e multas
  const fetchTeam = useCallback(async () => {
    if (!teamId) {
      setError("ID do time não fornecido.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      const getMethod = teamService.getById || teamService.getTeamById;
      const teamData = await getMethod(teamId, championshipIdFromContext);

      if (teamData && Object.keys(teamData).length > 0) {
        setTeam(teamData);
        if (teamData.badge) {
          setTeamImgSrc(getTeamLogoUrl(teamData.badge));
        } else {
          setTeamImgSrc('/default-team-badge.png');
        }

        // Buscar multas do time no campeonato atual
        const champId = championshipIdFromContext || teamData.championships?.[0]?._id;
        if (champId) {
          try {
            const finesRes = await penaltyService.getFinesByChampionship(champId, teamId);
            setTeamFines(finesRes.data || []);
          } catch (fineErr) {
            console.error('Erro ao buscar multas do time:', fineErr);
          }
        }

        await fetchManagersList();
      } else {
        setError('Time não encontrado.');
      }
    } catch (err) {
      console.error("Erro ao carregar informações do time:", err.response || err);
      setError(err.response?.data?.error || err.message || 'Erro ao carregar as informações do time');
    } finally {
      setIsLoading(false);
    }
  }, [teamId, championshipIdFromContext, fetchManagersList]);

  useEffect(() => {
    const fetchManagedChampionships = async () => {
      if (!user || !['Gestor', 'gestor', 'Admin', 'admin'].includes(user.role)) return;
      try {
        const allChampsResponse = await championshipService.getAll();
        const allChamps = allChampsResponse.data?.data || allChampsResponse.data || [];

        const managed = (allChamps || []).filter(champ =>
          String(champ.creator?._id) === String(user._id) ||
          (champ.managers || []).some(m => String(m._id) === String(user._id))
        ).map(c => c._id);

        setChampionshipsUserManages(managed);
      } catch (err) {
        console.error('Falha ao carregar campeonatos gerenciados:', err);
      }
    };
    if (user) fetchManagedChampionships();
  }, [user]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // Adicionar Gestor ao time
  const handleAddManager = async () => {
    if (!managerEmail.trim()) {
      alert('Insira um e-mail válido.');
      return;
    }
    setIsSubmittingManager(true);
    try {
      const response = await teamService.addManager(teamId, managerEmail.trim());
      if (response && response.success) {
        alert('Gestor adicionado com sucesso!');
        setManagerEmail('');
        setShowManagerModal(false);
        await fetchManagersList();
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Falha ao adicionar gestor.');
    } finally {
      setIsSubmittingManager(false);
    }
  };

  // Remover Gestor do time
  const handleRemoveManager = async (managerId, managerName) => {
    if (!window.confirm(`Remover "${managerName}" da comissão de gestores do time?`)) {
      return;
    }
    try {
      const response = await teamService.removeManager(teamId, managerId);
      if (response && response.success) {
        alert('Gestor removido com sucesso.');
        await fetchManagersList();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Não foi possível remover o gestor.');
    }
  };

  // Remover Jogador
  const handleRemovePlayerFromTeam = async (playerId, playerName) => {
    if (!canRemovePlayerFromThisTeam()) {
      alert("Você não tem permissão para remover jogadores deste time.");
      return;
    }
    if (!window.confirm(`Tem certeza que deseja remover o jogador "${playerName}" do time "${team?.name}"?`)) {
      return;
    }
    try {
      setIsLoading(true);
      await teamService.removePlayerFromTeam(teamId, playerId);
      await fetchTeam();
      alert(`Jogador "${playerName}" removido do time com sucesso.`);
    } catch (err) {
      setError(err.response?.data?.error || `Erro ao remover o jogador "${playerName}".`);
    } finally {
      setIsLoading(false);
    }
  };

  // Pagamento de Multa via Pix
  const handleGeneratePixPayment = async (fine) => {
    const athleteCpf = fine.player?.cpf;
    if (!athleteCpf) {
      alert(`O atleta ${fine.player?.name} não possui CPF cadastrado. Atualize o cadastro do jogador antes de gerar a cobrança Pix.`);
      return;
    }
    try {
      setLoadingPix(true);
      const response = await penaltyService.generateFinePayment(fine._id);
      if (response.success) {
        setPixPaymentData(response.pixData);
        setShowPixModal(true);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Não foi possível gerar a cobrança Pix.');
    } finally {
      setLoadingPix(false);
    }
  };

  const handleCopyPix = async () => {
    const code = pixPaymentData?.payload;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setPixCopied(true);
      setTimeout(() => setPixCopied(false), 2500);
    } catch (err) {
      alert('Não foi possível copiar o código Pix.');
    }
  };

  const closePixModal = () => {
    setShowPixModal(false);
    setPixPaymentData(null);
    setPixCopied(false);
  };

  const handleContinueToSheet = () => {
    setShowSheetOptionsModal(false);
    const currentChampId = championshipIdFromContext || (team.championships?.[0]?._id || team.championships?.[0]);

    navigate(`/teams/${teamId}/registration-sheet`, {
      state: {
        championshipId: currentChampId ? String(currentChampId) : undefined,
        includePhone: includePhoneInSheet,
        includeSignature: includeSignatureInSheet,
        includePosition: includePositionInSheet,
      },
    });
  };

  const getPlayerDisplayName = (player) => {
    if (!player) return 'Nome inválido';
    const displayName = player.nickname || player.name?.split(' ')[0] || 'Jogador';
    const shirtNumber = player.shirtNumber;
    return shirtNumber ? `${shirtNumber} - ${displayName}` : displayName;
  };

  if (isLoading) {
    return (
      <div className="team-detail-isolation-container">
        <div className="loading-container">
          <p className="loading-text">Carregando dados do time...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-detail-isolation-container">
        <div className="error-page">
          <h2 className="page-title error-title">Erro ao Carregar Time</h2>
          <p className="error-message-text">{error}</p>
          <button onClick={() => navigate('/teams')} className="button button-primary">
            Voltar para Lista de Times
          </button>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="team-detail-isolation-container">
        <div className="not-found-page">
          <h2 className="page-title">Time não encontrado</h2>
          <p>Não foi possível carregar os detalhes para o time solicitado.</p>
          <button onClick={() => navigate('/teams')} className="button button-primary">
            Voltar para Lista de Times
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="team-detail-isolation-container">
        <div className="team-detail-page-container">
          <h2 className="team-detail-page-title">Detalhes do Time</h2>

          <div className="team-detail-content-card">
            {/* Header */}
            <section className="team-header-section">
              <div className="team-logo-wrapper">
                <img
                  src={teamImgSrc}
                  alt={`Escudo do ${team.name || 'time'}`}
                  className="team-main-logo"
                  onError={() => setTeamImgSrc('/default-team-badge.png')}
                />
              </div>
              <div className="team-main-info">
                <h1 className="team-main-name">{team.name || 'Nome do Time Indisponível'}</h1>
                <p className="team-main-meta">{team.city || 'Cidade não informada'}</p>
                {team.foundedYear && (
                  <p className="team-main-meta">Fundado em {team.foundedYear}</p>
                )}
                <div className="team-player-count">
                  <p className="team-main-meta">
                    {countPlayers} jogador{countPlayers !== 1 ? 'es' : ''}
                    {technicalStaff.length > 0 && ` + ${technicalStaff.length} membro${technicalStaff.length !== 1 ? 's' : ''} da comissão técnica`}
                  </p>
                </div>
                {team.championships && team.championships.length > 0 && (
                  <div className={`registration-status-indicator ${isRegistrationOpen ? 'open' : 'closed'}`}>
                    {isRegistrationOpen ? (
                      <span className="badge badge-success">✅ Inscrições abertas em pelo menos um campeonato</span>
                    ) : (
                      <span className="badge badge-warning">🔒 Inscrições fechadas em todos os campeonatos</span>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Seção da Comissão Técnica */}
            {technicalStaff.length > 0 && (
              <section className="team-content-section staff-section">
                <h3 className="section-title">Comissão Técnica ({technicalStaff.length})</h3>
                <div className="player-list-grid">
                  {technicalStaff.map(member => (
                    <div key={member._id} className="player-card-item-wrapper">
                      <Link to={`/players/${member._id}`} className="player-card-link">
                        <div className="player-card-item staff-card-item">
                          <div className="player-photo-container">
                            <img
                              src={getPlayerPhotoUrl(member.photo)}
                              alt={member.name || 'Foto'}
                              className="player-list-photo"
                              onError={handlePlayerPhotoError}
                            />
                          </div>
                          <div className="player-list-info">
                            <p className="player-list-name staff-name">
                              {getPlayerDisplayName(member)}
                            </p>
                            <p className="player-list-position">
                              {member.position || 'Cargo não informado'}
                            </p>
                          </div>
                        </div>
                      </Link>

                      {canEditPlayerInscription() && member.inscriptionId && (
                        <Link
                          to={`/championship-player/${member.inscriptionId}/edit`}
                          className="button button-info btn-edit-player-inscription"
                          title={`Editar posição/número de ${member.name}`}
                        >
                          Editar Inscrição
                        </Link>
                      )}

                      {canRemovePlayerFromThisTeam() && (
                        <button
                          onClick={() => handleRemovePlayerFromTeam(member._id, member.name)}
                          className="button button-danger btn-remove-player-from-team"
                          title={`Remover ${member.name} deste time`}
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Seção de Campeonatos */}
            {team.championships && team.championships.length > 0 && (
              <section className="team-content-section championships-list-section">
                <h3 className="section-title">Participa dos Campeonatos</h3>
                <ul className="championships-list">
                  {team.championships.map(champ => (
                    <li key={champ._id} className="championship-item">
                      <Link to={`/championships/${champ._id}`} className="text-link">
                        {champ.name || 'Campeonato sem nome'}
                      </Link>
                      {champ.registrationOpen !== undefined && (
                        <span className={`championship-registration-badge ${champ.registrationOpen ? 'open' : 'closed'}`}>
                          {champ.registrationOpen ? '✅ Aberto' : '🔒 Fechado'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* ✅ SEÇÃO: COMISSÃO ADMINISTRATIVA (Gestores do Time) */}
            <section className="team-content-section managers-section" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 className="section-title" style={{ margin: 0 }}>Comissão Administrativa</h3>
                {canManageTeamManagers && (
                  <button
                    type="button"
                    onClick={() => setShowManagerModal(true)}
                    className="button button-secondary"
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    + Adicionar Gestor
                  </button>
                )}
              </div>

              {loadingManagers ? (
                <p style={{ color: '#666', fontSize: '13px' }}>Carregando gestores...</p>
              ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {/* Criador do Time */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div>
                      <strong>{typeof team.creator === 'object' ? team.creator?.name : 'Criador do Time'}</strong>
                      <span style={{ display: 'block', fontSize: '12px', color: '#64748b' }}>
                        📧 {typeof team.creator === 'object' && team.creator?.email ? team.creator.email : 'Email não disponível'}
                      </span>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: 'bold', backgroundColor: '#e2e8f0', color: '#475569', padding: '3px 8px', borderRadius: '4px' }}>
                      CRIADOR
                    </span>
                  </div>

                  {/* Demais Gestores */}
                  {managers.filter(m => String(m._id) !== String(team.creator?._id || team.creator)).map(manager => (
                    <div key={manager._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div>
                        <strong>{manager.name}</strong>
                        <span style={{ display: 'block', fontSize: '12px', color: '#64748b' }}>
                          📧 {manager.email}
                        </span>
                      </div>
                      {canManageTeamManagers && (
                        <button
                          type="button"
                          onClick={() => handleRemoveManager(manager._id, manager.name)}
                          style={{ backgroundColor: 'transparent', border: 'none', color: '#e53e3e', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}
                          title="Remover gestor"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Seção de Jogadores */}
            <section className="team-content-section players-list-section">
              <h3 className="section-title">
                Jogadores {countPlayers > 0 && `(${countPlayers})`}
              </h3>

              {filteredPlayers.length > 0 ? (
                <ul className="player-list-grid">
                  {filteredPlayers.map((player) => (
                    <li key={player._id} className="player-card-item-wrapper">
                      <Link to={`/players/${player._id}`} className="player-card-link">
                        <div className="player-card-item">
                          <div className="player-photo-container">
                            <img
                              src={getPlayerPhotoUrl(player.photo)}
                              alt={player.name || 'Foto do Jogador'}
                              className="player-list-photo"
                              onError={handlePlayerPhotoError}
                            />
                          </div>
                          <div className="player-list-info">
                            <p className="player-list-name">{getPlayerDisplayName(player)}</p>
                            <p className="player-list-position">{player.position || 'Posição não informada'}</p>
                          </div>
                        </div>
                      </Link>

                      {canEditPlayerInscription() && player.inscriptionId && (
                        <Link
                          to={`/championship-player/${player.inscriptionId}/edit`}
                          className="button button-info btn-edit-player-inscription"
                          title={`Editar posição/número de ${player.name}`}
                        >
                          Editar Inscrição
                        </Link>
                      )}

                      {canRemovePlayerFromThisTeam() && (
                        <button
                          onClick={() => handleRemovePlayerFromTeam(player._id, player.name)}
                          className="button button-danger btn-remove-player-from-team"
                          title={`Remover ${player.name} deste time`}
                        >
                          Remover
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="empty-list-message">Nenhum jogador vinculado a este time.</p>
              )}
            </section>

            {/* ✅ SEÇÃO: MULTAS POR CARTÕES */}
            <section className="team-content-section fines-section" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
              <h3 className="section-title">Multas por Cartões</h3>
              {teamFines && teamFines.length > 0 ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {teamFines.map((fine) => (
                    <div key={fine._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #e2e8f0', padding: '12px', borderRadius: '8px' }}>
                      <div>
                        <strong style={{ fontSize: '14px', color: '#1e293b' }}>{fine.player?.name || 'Atleta'}</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                          {fine.reason === 'direct_red' ? '🟥 Cartão Vermelho Direto' : '🟨 Acúmulo de Cartões Amarelos'}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#e53e3e' }}>
                          R$ {fine.amount?.toFixed(2)}
                        </div>
                        <span style={{
                          display: 'inline-block',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          marginTop: '4px',
                          backgroundColor: fine.status === 'paid' ? '#d4edda' : '#f8d7da',
                          color: fine.status === 'paid' ? '#155724' : '#721c24'
                        }}>
                          {fine.status === 'paid' ? 'PAGO' : 'PENDENTE'}
                        </span>

                        {fine.status === 'pending' && (
                          <div style={{ marginTop: '6px' }}>
                            <button
                              type="button"
                              onClick={() => handleGeneratePixPayment(fine)}
                              className="button button-success"
                              disabled={loadingPix}
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                            >
                              {loadingPix ? 'Gerando...' : 'Pagar Pix'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-list-message" style={{ margin: 0 }}>Nenhuma multa registrada para este time.</p>
              )}
            </section>

            {/* Mensagens de bloqueio/informação sobre inscrições */}
            {isAuthenticated && !canRegisterPlayer && team.championships && team.championships.length > 0 && (
              <div className="info-message" style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: 'rgba(255, 193, 7, 0.12)',
                borderLeft: '4px solid #ffc107',
                borderRadius: '4px'
              }}>
                <p style={{ margin: 0, color: '#856404', fontSize: '13px' }}>
                  {!isRegistrationOpen
                    ? '⚠️ Não é possível inscrever novos jogadores pois todos os campeonatos que este time participa estão com as inscrições fechadas.'
                    : isSelfRegistrationBlockedForPlayer
                    ? '⚠️ A auto-inscrição direta por atletas está desativada neste campeonato. Solicite à diretoria ou comissão técnica do seu time que realize a sua inscrição.'
                    : '⚠️ Você não tem permissão para inscrever jogadores neste time.'}
                </p>
              </div>
            )}

            {/* BOTÕES DE AÇÃO DO RODAPÉ */}
            <div className="action-buttons-footer" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '25px' }}>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="button button-secondary"
              >
                Voltar
              </button>

              {canEditTeam && (
                <button
                  type="button"
                  onClick={() => navigate(`/teams/${teamId}/edit`)}
                  className="button button-primary"
                >
                  Editar Time
                </button>
              )}

              {canGenerateRegistrationSheet && (
                <button
                  type="button"
                  onClick={() => setShowSheetOptionsModal(true)}
                  className="button button-success"
                  style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
                >
                  Gerar Ficha de Inscrição
                </button>
              )}

              {/* Botões do Mercado de Transferências */}
              {isTeamOwnerOrManager && activeTransferChampionship && (
                <>
                  <button
                    type="button"
                    onClick={() => navigate(`/championships/${activeTransferChampionship._id}?tab=transfer-market`)}
                    className="button"
                    style={{ backgroundColor: '#ff9500', color: '#000', fontWeight: 'bold' }}
                  >
                    🔄 Buscar Reforços (Empréstimo)
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate(`/teams/${teamId}/proposals`)}
                    className="button"
                    style={{ backgroundColor: '#17a2b8', color: '#fff' }}
                  >
                    📋 Painel de Negociações
                  </button>
                </>
              )}

              {canRegisterPlayer && (
                <button
                  type="button"
                  onClick={() => {
                    const currentChampId =
                      championshipIdFromContext ||
                      (team.championships?.[0]?._id || team.championships?.[0]);

                    navigate(`/teams/${teamId}/players/new`, {
                      state: { championshipId: currentChampId ? String(currentChampId) : undefined }
                    });
                  }}
                  className="button button-primary"
                  style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                >
                  Inscrever Jogador
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL ADICIONAR GESTOR */}
      {showManagerModal && (
        <div className="sheet-options-modal-overlay" onClick={() => setShowManagerModal(false)}>
          <div className="sheet-options-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="sheet-options-modal-header">
              <div className="sheet-options-modal-icon">👥</div>
              <div className="sheet-options-modal-heading">
                <h3 className="sheet-options-modal-title">Adicionar Gestor ao Time</h3>
                <p className="sheet-options-modal-subtitle">
                  Insira o e-mail de um usuário cadastrado para conceder acesso administrativo a este time.
                </p>
              </div>
            </div>

            <div style={{ padding: '15px 0' }}>
              <input
                type="email"
                placeholder="email@exemplo.com"
                value={managerEmail}
                onChange={(e) => setManagerEmail(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div className="sheet-options-modal-actions">
              <button
                type="button"
                className="sheet-modal-btn secondary"
                onClick={() => { setShowManagerModal(false); setManagerEmail(''); }}
                disabled={isSubmittingManager}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="sheet-modal-btn primary"
                onClick={handleAddManager}
                disabled={isSubmittingManager}
              >
                {isSubmittingManager ? 'Adicionando...' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PAGAMENTO PIX */}
      {showPixModal && (
        <div className="sheet-options-modal-overlay" onClick={closePixModal}>
          <div className="sheet-options-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
            <div className="sheet-options-modal-header">
              <div className="sheet-options-modal-icon">💸</div>
              <div className="sheet-options-modal-heading">
                <h3 className="sheet-options-modal-title">Pagamento via Pix</h3>
                <p className="sheet-options-modal-subtitle">
                  Escaneie o QR Code no app do seu banco ou copie o código Pix abaixo.
                </p>
              </div>
            </div>

            <div style={{ margin: '15px auto', padding: '10px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', display: 'inline-block' }}>
              {getPixQrSource(pixPaymentData?.encodedImage) ? (
                <img
                  src={getPixQrSource(pixPaymentData.encodedImage)}
                  alt="QR Code Pix"
                  style={{ width: '180px', height: '180px', display: 'block' }}
                />
              ) : (
                <p style={{ margin: '20px', fontSize: '13px', color: '#666' }}>Carregando QR Code...</p>
              )}
            </div>

            {pixPaymentData?.payload && (
              <div style={{ textAlign: 'left', marginTop: '10px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569' }}>Pix Copia e Cola:</label>
                <textarea
                  readOnly
                  value={pixPaymentData.payload}
                  style={{ width: '100%', height: '60px', padding: '8px', fontSize: '11px', border: '1px solid #cbd5e1', borderRadius: '6px', resize: 'none', backgroundColor: '#f8fafc', boxSizing: 'border-box', marginTop: '4px' }}
                />
                <button
                  type="button"
                  onClick={handleCopyPix}
                  className="button button-success"
                  style={{ width: '100%', marginTop: '8px', padding: '10px', fontSize: '13px' }}
                >
                  {pixCopied ? '✅ Código Copiado com Sucesso!' : '📋 Copiar Código Pix'}
                </button>
              </div>
            )}

            <div className="sheet-options-modal-actions" style={{ marginTop: '15px' }}>
              <button
                type="button"
                className="sheet-modal-btn secondary"
                onClick={closePixModal}
                style={{ width: '100%' }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE OPÇÕES DA FICHA DE INSCRIÇÃO */}
      {showSheetOptionsModal && (
        <div
          className="sheet-options-modal-overlay"
          onClick={() => setShowSheetOptionsModal(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="sheet-options-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sheet-options-modal-header">
              <div className="sheet-options-modal-icon">📋</div>
              <div className="sheet-options-modal-heading">
                <h3 className="sheet-options-modal-title">Configurar Ficha de Inscrição</h3>
                <p className="sheet-options-modal-subtitle">
                  Escolha quais informações devem aparecer nas tabelas da ficha.
                </p>
              </div>
              <button
                type="button"
                className="sheet-options-modal-close"
                onClick={() => setShowSheetOptionsModal(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            <div className="sheet-options-list">
              <label className={`sheet-option-row ${includePhoneInSheet ? 'is-active' : ''}`}>
                <span className="sheet-option-icon">📞</span>
                <span className="sheet-option-text">
                  <span className="sheet-option-title">Telefone</span>
                  <span className="sheet-option-description">
                    Exibe o telefone de contato de atletas e comissão técnica.
                  </span>
                </span>
                <input
                  type="checkbox"
                  className="sheet-option-input"
                  checked={includePhoneInSheet}
                  onChange={(e) => setIncludePhoneInSheet(e.target.checked)}
                />
                <span className="sheet-toggle" aria-hidden="true">
                  <span className="sheet-toggle-knob" />
                </span>
              </label>

              <label className={`sheet-option-row ${includeSignatureInSheet ? 'is-active' : ''}`}>
                <span className="sheet-option-icon">✍️</span>
                <span className="sheet-option-text">
                  <span className="sheet-option-title">Assinatura</span>
                  <span className="sheet-option-description">
                    Adiciona uma coluna em branco para assinatura física na ficha.
                  </span>
                </span>
                <input
                  type="checkbox"
                  className="sheet-option-input"
                  checked={includeSignatureInSheet}
                  onChange={(e) => setIncludeSignatureInSheet(e.target.checked)}
                />
                <span className="sheet-toggle" aria-hidden="true">
                  <span className="sheet-toggle-knob" />
                </span>
              </label>

              <label className={`sheet-option-row ${includePositionInSheet ? 'is-active' : ''}`}>
                <span className="sheet-option-icon">⚽</span>
                <span className="sheet-option-text">
                  <span className="sheet-option-title">Posição do Atleta</span>
                  <span className="sheet-option-description">
                    Mostra a posição de cada jogador (Goleiro, Zagueiro, Atacante…).
                  </span>
                </span>
                <input
                  type="checkbox"
                  className="sheet-option-input"
                  checked={includePositionInSheet}
                  onChange={(e) => setIncludePositionInSheet(e.target.checked)}
                />
                <span className="sheet-toggle" aria-hidden="true">
                  <span className="sheet-toggle-knob" />
                </span>
              </label>
            </div>

            <div className="sheet-options-preview">
              <div className="sheet-options-preview-label">Prévia das colunas</div>
              <div className="sheet-options-preview-table">
                <span className="preview-col">Nº</span>
                <span className="preview-col">Nome</span>
                {includePositionInSheet && <span className="preview-col">Posição</span>}
                {includePhoneInSheet && <span className="preview-col">Telefone</span>}
                {includeSignatureInSheet && <span className="preview-col">Assinatura</span>}
              </div>
            </div>

            <div className="sheet-options-modal-actions">
              <button
                type="button"
                className="sheet-modal-btn secondary"
                onClick={() => setShowSheetOptionsModal(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="sheet-modal-btn primary"
                onClick={handleContinueToSheet}
              >
                Gerar Ficha
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TeamDetail;